import { defaultServiceLanguage, type ServiceLanguage } from "@albertine/i18n";
import { createContext, type ReactNode, use } from "react";

import { type ChatMessageLabels, defaultChatMessageLabels } from "./labels";

/**
 * Lets the host show a tapped image itself. Returning `true` keeps the web
 * lightbox closed; `false` falls back to it.
 */
type OpenImageHandler = (url: string) => boolean;

type ChatContextValue = {
    lang: ServiceLanguage;
    labels: ChatMessageLabels;
    onImageLightboxChange?: (open: boolean) => void;
    onOpenImage?: OpenImageHandler;
};

const ChatContext = createContext<ChatContextValue>({
    lang: defaultServiceLanguage,
    labels: defaultChatMessageLabels,
});

type ChatProviderProps = {
    children: ReactNode;
    lang?: ServiceLanguage;
    labels?: ChatMessageLabels;
    onImageLightboxChange?: (open: boolean) => void;
    onOpenImage?: OpenImageHandler;
};

export function ChatProvider({
    children,
    lang = defaultServiceLanguage,
    labels = defaultChatMessageLabels,
    onImageLightboxChange,
    onOpenImage,
}: ChatProviderProps) {
    return (
        <ChatContext value={{ lang, labels, onImageLightboxChange, onOpenImage }}>
            {children}
        </ChatContext>
    );
}

export function useChat() {
    return use(ChatContext);
}
